import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { getChallenges, getMediaPage } from '../api/confetti'
import { MediaGrid } from '../components/MediaGrid'
import { CameraIcon, GridIcon, Spinner } from '../components/icons'
import { plural } from '../lib/format'
import { useSession } from '../session/context'
import { useAlbum } from '../layouts/AlbumContext'

/**
 * Ronde 22E — één challenge, met wat andere gasten er al bij zetten.
 *
 * De twee knoppen openen hetzelfde paneel als elders in de app; wat je hier
 * uploadt wordt aan deze challenge gekoppeld. Daaronder staat het werk van
 * anderen, want dat is waar je het meeste zin van krijgt om zelf iets te doen.
 */
export function ChallengeDetailPage() {
  const { challengeId = '' } = useParams()
  const { slug, guest } = useSession()
  const { openPanel, readOnly } = useAlbum()
  const navigate = useNavigate()

  const challenges = useQuery({
    queryKey: ['challenges', slug, guest?.guestId ?? null],
    queryFn: ({ signal }) => getChallenges(slug, guest?.guestId, signal),
  })

  const list = challenges.data?.challenges ?? []
  const index = list.findIndex((entry) => entry.id === challengeId)
  const challenge = index >= 0 ? list[index] : null

  const media = useInfiniteQuery({
    queryKey: ['media', slug, 'challenge', challengeId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => getMediaPage(slug, { challengeId }, pageParam, signal),
    getNextPageParam: (page) => page.nextCursor,
    enabled: challengeId !== '',
  })

  const items = media.data?.pages.flatMap((page) => page.items) ?? []

  if (challenges.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center py-20">
        <Spinner className="text-accent size-6" />
      </div>
    )
  }

  if (!challenge) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-8 py-20 text-center">
        <p className="font-display text-[26px]">Die challenge bestaat niet meer</p>
        <button
          type="button"
          onClick={() => navigate('..', { relative: 'path' })}
          className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink mt-5 rounded-full border px-5 py-2.5 text-sm transition-colors"
        >
          Terug naar de lijst
        </button>
      </div>
    )
  }

  const wantsVideo = challenge.kind === 'Video'

  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-5 pb-28 lg:px-9 lg:pt-7 lg:pb-9">
      <header className="flex items-center gap-3.5 lg:pr-64">
        <button
          type="button"
          aria-label="Terug"
          onClick={() => navigate('..', { relative: 'path' })}
          className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink flex size-9.5 shrink-0 items-center justify-center rounded-full border transition-colors"
        >
          ←
        </button>
        <p className="text-ink-faint text-[13px]">
          Challenge {index + 1} van {list.length}
        </p>
      </header>

      <h1 className="font-display mt-5 text-[32px] leading-tight lg:text-[40px]">
        {challenge.title}
      </h1>
      <p className="text-ink-soft mt-1.5 text-sm lg:text-[15px]">
        {challenge.kind === 'Video' ? 'Video' : challenge.kind === 'Photo' ? 'Foto' : 'Allebei'}
        {challenge.submissionCount > 0 &&
          ` · ${plural(challenge.submissionCount, 'gast', 'gasten')} deden dit`}
      </p>

      {/* Meekijken is geen meedoen: in een privé galerij ziet de eigenaar wat
          er ingestuurd is, maar krijgt hij geen knop om er zelf iets bij te
          zetten — dat zou de server toch weigeren. */}
      {!readOnly && (
        <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:gap-3">
          <button
            type="button"
            onClick={() => openPanel('toevoegen', challenge.id)}
            className="bg-accent text-accent-ink shadow-accent flex h-13.5 cursor-pointer items-center justify-center gap-2.5 rounded-full px-6.5 text-base font-semibold transition hover:brightness-95"
          >
            <CameraIcon className="size-5" />
            {wantsVideo ? 'Neem een video' : 'Neem een foto'}
          </button>

          <button
            type="button"
            onClick={() => openPanel('toevoegen', challenge.id)}
            className="border-line text-ink-muted hover:bg-tint/50 hover:text-ink flex h-13.5 cursor-pointer items-center justify-center gap-2.5 rounded-full border px-6.5 text-base transition-colors"
          >
            <GridIcon className="size-4.5" />
            Kies uit mijn bestanden
          </button>
        </div>
      )}

      <p className="text-ink-faint mt-8 mb-3 text-[11px] tracking-[0.18em] uppercase lg:text-xs">
        Van andere gasten
      </p>

      {media.isPending ? (
        <div className="flex justify-center py-12">
          <Spinner className="text-accent size-5" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-ink-soft py-10 text-center text-sm">
          Nog niemand deed deze challenge. Wees de eerste.
        </p>
      ) : (
        <>
          <p className="text-ink-soft mb-3 text-[13px]">{plural(items.length, 'foto', "foto's")}</p>

          <MediaGrid
            items={items}
            pending={[]}
            onOpen={(id) => navigate(`../../album/${id}`, { relative: 'path' })}
          />

          {media.hasNextPage && (
            <button
              type="button"
              onClick={() => void media.fetchNextPage()}
              className="text-accent mt-5 w-full py-3 text-center text-[15px]"
            >
              {media.isFetchingNextPage ? 'Laden…' : 'Meer laden'}
            </button>
          )}
        </>
      )}
    </div>
  )
}
